import { Head, Link } from '@inertiajs/react'
import { ArrowRight, GraduationCap, MessageCircle, Sparkles } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { StatCard } from '@/components/cards/StatCard'
import { Card } from '@/components/ui/Card'
import { Button, buttonClasses } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import type { Breadcrumb } from '@/types/shared'

type Stats = {
  totalPlayers: number
  updatedThisWeek: number
  activeChatrooms: number
}

type RecentPlayer = {
  id: string
  name: string
  prefecture?: string | null
  teamName?: string | null
  positions: string[]
  height?: number | null
  dominantFoot?: string | null
  dominantFootLabel?: string | null
  fiftyMeterTime?: number | null
  updatedAt: string
}

type RecentChatroom = {
  id: string
  playerName?: string | null
  teamName?: string | null
  lastMessage?: string | null
  lastMessageAt?: string | null
  lastMessageSender?: string | null
}

type Tip = {
  title: string
  body: string
}

export type SchoolDashboardProps = {
  stats: Stats
  recentPlayers: RecentPlayer[]
  recentChatrooms: RecentChatroom[]
  tips: Tip[]
  breadcrumbs: Breadcrumb[]
}

export default function SchoolDashboard({ stats, recentPlayers, recentChatrooms, tips, breadcrumbs }: SchoolDashboardProps) {
  return (
    <AppShell>
      <Head title="高校ダッシュボード" />
      <PageHeader
        title="高校ダッシュボード"
        description="注目の選手やチャットの進捗を一目で把握し、スカウティングをスピーディーに進めましょう。"
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex items-center gap-3">
            <Button asChild variant="secondary" size="sm">
              <Link href="/school/players">
                選手を探す
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="sm">
              <Link href="/school/chatrooms">
                チャット一覧
                <MessageCircle className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        }
      />

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard
          label="登録選手総数"
          value={stats.totalPlayers}
          delta={{ value: 'プラットフォーム全体', trend: 'steady' }}
          icon={<GraduationCap className="h-5 w-5" />}
        />
        <StatCard
          label="今週更新された選手"
          value={stats.updatedThisWeek}
          delta={{ value: '最新プロフィールを優先的に確認', trend: 'up' }}
          icon={<Sparkles className="h-5 w-5" />}
        />
        <StatCard
          label="アクティブチャット"
          value={stats.activeChatrooms}
          delta={{ value: '返信が止まっていないか確認', trend: 'steady' }}
          icon={<MessageCircle className="h-5 w-5" />}
        />
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card className="space-y-4" padding="lg">
          <header className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">最近更新された選手</h2>
              <p className="mt-1 text-xs text-white/50">最新のプロフィール更新順に表示しています</p>
            </div>
            <Link href="/school/players" className={buttonClasses({ variant: 'ghost', size: 'sm' })}>
              すべて見る
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </header>

          {recentPlayers.length === 0 ? (
            <EmptyState
              title="まだ更新情報がありません"
              description="プロフィールが更新されるとここに表示されます。通知設定で見逃しを防ぎましょう。"
            />
          ) : (
            <div className="space-y-3">
              {recentPlayers.map((player) => (
                <Link
                  key={player.id}
                  href={`/school/players/${player.id}`}
                  className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 transition hover:border-white/20 hover:bg-white/10"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-white">{player.name}</p>
                      <p className="text-xs text-white/60">
                        {(player.teamName ?? '所属未登録') +
                          ' · ' +
                          (player.prefecture ?? '所在地未登録')}
                      </p>
                    </div>
                    <span className="text-[11px] text-white/40">{formatDate(player.updatedAt)} 更新</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-white/70">
                    {player.positions.length ? (
                      player.positions.map((position) => (
                        <span key={position} className="rounded-full bg-brand-500/15 px-2 py-1 text-brand-100">
                          {position}
                        </span>
                      ))
                    ) : (
                      <span className="rounded-full bg-white/10 px-2 py-1 text-white/50">ポジション未設定</span>
                    )}
                    <span className="rounded-full bg-white/10 px-2 py-1">
                      {player.height ? `${player.height} cm` : '身長未登録'}
                    </span>
                    <span className="rounded-full bg-white/10 px-2 py-1">{player.dominantFootLabel || '利き足未設定'}</span>
                    <span className="rounded-full bg-white/10 px-2 py-1">{formatSprint(player.fiftyMeterTime)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card className="space-y-4" padding="lg">
          <header className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">最近のチャット</h2>
            <Link href="/school/chatrooms" className={buttonClasses({ variant: 'ghost', size: 'sm' })}>
              一覧
            </Link>
          </header>
          {recentChatrooms.length === 0 ? (
            <EmptyState
              title="チャット履歴がありません"
              description="気になる選手の詳細ページからコンタクトを開始できます。"
            />
          ) : (
            <div className="space-y-3">
              {recentChatrooms.map((chatroom) => (
                <Link
                  key={chatroom.id}
                  href={`/school/chatrooms/${chatroom.id}`}
                  className="block rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/80 transition hover:border-white/20 hover:bg-white/10"
                >
                  <div className="flex items-center justify-between gap-3 text-white">
                    <span className="font-semibold">{chatroom.playerName}</span>
                    {chatroom.lastMessageAt ? (
                      <time className="text-xs text-white/40">{formatDateTime(chatroom.lastMessageAt)}</time>
                    ) : null}
                  </div>
                  <p className="text-xs text-white/60">{chatroom.teamName ?? '所属未登録'}</p>
                  <p className="mt-2 line-clamp-2 text-xs text-white/70">
                    {chatroom.lastMessage ? chatroom.lastMessage : 'メッセージはまだありません'}
                  </p>
                  {chatroom.lastMessageSender ? (
                    <p className="mt-1 text-[11px] text-white/40">{chatroom.lastMessageSender}</p>
                  ) : null}
                </Link>
              ))}
            </div>
          )}
        </Card>
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {tips.map((tip) => (
          <Card key={tip.title} className="space-y-2" padding="lg">
            <h3 className="text-base font-semibold text-white">{tip.title}</h3>
            <p className="text-sm text-white/70 leading-relaxed">{tip.body}</p>
          </Card>
        ))}
      </section>
    </AppShell>
  )
}

function formatDate(value: string) {
  return new Date(value).toLocaleDateString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
  })
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatSprint(value?: number | null) {
  if (value == null) return '50m未登録'
  const numeric = Number(value)
  if (Number.isNaN(numeric)) return '50m未登録'
  return `${numeric.toFixed(1)} 秒`
}
